/**
 * GovernanceRules — Rule configuration panel for the Governance page.
 *
 * FUI codes, each toggleable with severity and options.
 * Cloud policy is the default; local config file overrides.
 */
/* eslint-disable max-lines */
import * as React from 'react';
import {
  CaretDown,
  CaretRight,
  CircleNotch,
  Info,
  ShieldCheck,
  Warning,
} from '@phosphor-icons/react';
import { Button, Card, Stack, Text } from '@fragments-sdk/ui';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { useClerkUser } from '@/hooks/useClerkUser';
import { useOrgContext } from '@/hooks/useOrgContext';
import {
  DEFAULT_CODE_STATES,
  RULE_TO_CONVEX,
  severityFromCode,
  type CodePolicyState,
  type ConvexRuleKey,
  type EditableRuleId,
  type RuleState,
  type Severity,
} from '@/lib/govern-rules';
import {
  GOVERNANCE_CODE_CATALOG,
  type GovernanceCodeCatalogEntry,
} from '@/lib/govern-code-catalog';
import {
  PLANS,
  cheapestPlanWithCloudPolicyManagement,
  hasCloudPolicyManagement,
  type PlanId,
} from '../../../../convex/billing/plans';
import s from './governance.module.scss';

type PolicyState = CodePolicyState;

type PolicySourceStatus =
  | 'declared'
  | 'inferred'
  | 'generic_default'
  | 'missing'
  | 'not_required';

type GovernancePolicyQueryData = {
  policy: {
    rules?: Partial<Record<ConvexRuleKey, RuleState>>;
    codes?: Partial<CodePolicyState>;
    canonicalSources?: Array<Record<string, unknown>>;
    updatedAt?: number;
  } | null;
  configDetected: boolean;
  tokenPrefix?: string | null;
  canonicalPackageName?: string | null;
  dsType?: string | null;
  governancePreset?: string | null;
  tokenConfig?: {
    sources?: Array<{
      filePath?: string;
      format?: string;
      isPrimary?: boolean;
    }>;
    prefix?: string;
    stylingApproach?: string;
    thirdPartyDS?: string;
  } | null;
  codebaseProfile?: {
    styling?: { primary?: string; secondary?: string[] };
    tokens?: {
      format?: string;
      sources?: Array<{ filePath?: string; format?: string }>;
      totalCount?: number;
      primaryPrefix?: string;
    };
    thirdPartyDS?: { name?: string; hasCustomTheme?: boolean };
    componentRoots?: Array<{
      path?: string;
      componentCount?: number;
      looksLikeDesignSystem?: boolean;
    }>;
  } | null;
};

type PolicyReadinessItem = {
  id: string;
  label: string;
  detail: string;
  status: PolicySourceStatus;
};

type PolicyStatusCounts = Record<PolicySourceStatus, number>;

const POLICY_STATUS_LABELS: Record<PolicySourceStatus, string> = {
  declared: 'Declared',
  inferred: 'Inferred',
  generic_default: 'Generic',
  missing: 'Policy needed',
  not_required: 'Not required',
};

const POLICY_INDEPENDENT_RULES = new Set<EditableRuleId>([
  'a11y/wcag',
  'tailwind/arbitrary-color',
  'tailwind/arbitrary-spacing',
  'tailwind/unknown-class',
]);
const FORCE_POLICY_SAVE_FAILURE_KEY = '__fragments_force_policy_save_failure__';
const TEST_MODE_ACTIVE =
  import.meta.env.DEV && import.meta.env.VITE_FRAGMENTS_TEST_MODE === '1';

function shouldForcePolicySaveFailure(): boolean {
  if (!TEST_MODE_ACTIVE || typeof window === 'undefined') return false;
  return window.sessionStorage.getItem(FORCE_POLICY_SAVE_FAILURE_KEY) === '1';
}

function getDefaultPolicy(): PolicyState {
  return { ...DEFAULT_CODE_STATES };
}

function policyFromConvex(
  raw: Record<
    string,
    { enabled: boolean; severity: string; options?: unknown }
  > | null,
  codes?: Partial<CodePolicyState> | null,
): PolicyState {
  const defaults = getDefaultPolicy();

  for (const entry of GOVERNANCE_CODE_CATALOG) {
    const fallbackKey = entry.policyKey;
    const stored =
      codes?.[entry.code] ?? (fallbackKey ? raw?.[fallbackKey] : undefined);
    if (stored) {
      defaults[entry.code] = {
        enabled: stored.enabled,
        severity: stored.severity as Severity,
        options: (stored.options as Record<string, unknown>) ?? undefined,
      };
    }
  }
  return defaults;
}

function policyToConvexCodes(policy: PolicyState): CodePolicyState {
  const result: CodePolicyState = {};
  for (const entry of GOVERNANCE_CODE_CATALOG) {
    result[entry.code] = policy[entry.code] ?? {
      enabled: true,
      severity: severityFromCode(entry.defaultSeverity),
    };
  }
  return result;
}

function hasStoredCode(
  policyData: GovernancePolicyQueryData,
  entry: GovernanceCodeCatalogEntry,
) {
  return Boolean(
    policyData.policy?.codes?.[entry.code] ||
    (entry.policyKey && policyData.policy?.rules?.[entry.policyKey]),
  );
}

function hasTokenInference(policyData: GovernancePolicyQueryData) {
  const profile = policyData.codebaseProfile;
  return Boolean(
    policyData.tokenPrefix ||
    policyData.tokenConfig?.prefix ||
    (policyData.tokenConfig?.sources?.length ?? 0) > 0 ||
    (profile?.tokens?.sources?.length ?? 0) > 0 ||
    (profile?.tokens?.totalCount ?? 0) > 0,
  );
}

function hasTailwindInference(policyData: GovernancePolicyQueryData) {
  const stylingPrimary = policyData.codebaseProfile?.styling?.primary ?? '';
  const tokenFormat = policyData.codebaseProfile?.tokens?.format ?? '';
  const tokenConfigStyle = policyData.tokenConfig?.stylingApproach ?? '';
  return [
    policyData.dsType,
    policyData.governancePreset,
    stylingPrimary,
    tokenFormat,
    tokenConfigStyle,
  ].some((value) => value?.toLowerCase().includes('tailwind'));
}

function hasComponentInference(policyData: GovernancePolicyQueryData) {
  const componentRoots = policyData.codebaseProfile?.componentRoots ?? [];
  return Boolean(
    (policyData.policy?.canonicalSources?.length ?? 0) > 0 ||
    policyData.canonicalPackageName ||
    componentRoots.some(
      (root) =>
        (root.componentCount ?? 0) > 0 || root.looksLikeDesignSystem === true,
    ),
  );
}

function hasStoredLegacyRule(
  policyData: GovernancePolicyQueryData,
  ruleId: EditableRuleId,
) {
  return Boolean(policyData.policy?.rules?.[RULE_TO_CONVEX[ruleId]]);
}

function getCodePolicyStatus(
  entry: GovernanceCodeCatalogEntry,
  policyData: GovernancePolicyQueryData,
): PolicySourceStatus {
  const ruleId = entry.ruleId as EditableRuleId;
  if (hasStoredCode(policyData, entry)) return 'declared';
  if (!isEditableRuleId(ruleId)) {
    if (entry.policyKey === 'a11y_wcag') return 'not_required';
    if (entry.family === 'tokens' || entry.family === 'styles') {
      return hasTokenInference(policyData) ? 'inferred' : 'generic_default';
    }
    if (entry.family === 'components') {
      return hasComponentInference(policyData) ? 'inferred' : 'missing';
    }
    if (entry.family === 'tailwind') {
      return hasTailwindInference(policyData) ? 'inferred' : 'generic_default';
    }
    return 'generic_default';
  }
  if (POLICY_INDEPENDENT_RULES.has(ruleId)) return 'not_required';

  if (ruleId === 'tokens/hardcoded-values') {
    return hasTokenInference(policyData) ? 'inferred' : 'generic_default';
  }

  if (ruleId === 'components/usage') {
    return hasComponentInference(policyData) ? 'inferred' : 'missing';
  }

  if (ruleId.startsWith('tailwind/')) {
    return hasTailwindInference(policyData) ? 'inferred' : 'generic_default';
  }

  return 'generic_default';
}

function isEditableRuleId(ruleId: string): ruleId is EditableRuleId {
  return Object.prototype.hasOwnProperty.call(RULE_TO_CONVEX, ruleId);
}

function buildPolicyReadinessItems(
  policyData: GovernancePolicyQueryData,
): PolicyReadinessItem[] {
  const explicitCanonicalSources =
    (policyData.policy?.canonicalSources?.length ?? 0) > 0;
  const derivedCanonicalSource = Boolean(policyData.canonicalPackageName);

  return [
    {
      id: 'tokens',
      label: 'Token sources',
      status: hasStoredLegacyRule(policyData, 'tokens/hardcoded-values')
        ? 'declared'
        : hasTokenInference(policyData)
          ? 'inferred'
          : 'generic_default',
      detail: hasTokenInference(policyData)
        ? formatTokenSignal(policyData)
        : 'No token source has been detected yet.',
    },
    {
      id: 'tailwind',
      label: 'Tailwind theme',
      status: hasTailwindInference(policyData) ? 'inferred' : 'missing',
      detail: hasTailwindInference(policyData)
        ? 'Tailwind styling or token configuration was detected in this project.'
        : 'Tailwind policy-dependent rules need theme or config evidence.',
    },
    {
      id: 'components',
      label: 'Component policy',
      status: explicitCanonicalSources
        ? 'declared'
        : derivedCanonicalSource || hasComponentInference(policyData)
          ? 'inferred'
          : 'missing',
      detail: explicitCanonicalSources
        ? 'Canonical component sources are configured in governance policy.'
        : derivedCanonicalSource
          ? `Canonical source inferred from ${policyData.canonicalPackageName}.`
          : 'No canonical component source or registry signal is ready.',
    },
    {
      id: 'a11y',
      label: 'Accessibility standard',
      status: 'not_required',
      detail:
        'WCAG checks are source-evidence based and do not need token policy.',
    },
  ];
}

function formatTokenSignal(policyData: GovernancePolicyQueryData) {
  const tokenSource = policyData.tokenConfig?.sources?.[0];
  const profileSource = policyData.codebaseProfile?.tokens?.sources?.[0];
  const source = tokenSource?.filePath ?? profileSource?.filePath;
  if (source) return `Primary token signal: ${source}.`;
  const totalCount = policyData.codebaseProfile?.tokens?.totalCount ?? 0;
  if (totalCount > 0) return `${totalCount} tokens detected from scan signals.`;
  return 'Token prefix or source configuration is present.';
}

function emptyPolicyStatusCounts(): PolicyStatusCounts {
  return {
    declared: 0,
    inferred: 0,
    generic_default: 0,
    missing: 0,
    not_required: 0,
  };
}

function buildCodePolicyStatusCounts(
  policyData: GovernancePolicyQueryData,
): PolicyStatusCounts {
  const counts = emptyPolicyStatusCounts();
  for (const entry of GOVERNANCE_CODE_CATALOG) {
    counts[getCodePolicyStatus(entry, policyData)] += 1;
  }
  return counts;
}

// ── Severity picker ─────────────────────────────────────────────

const SEVERITY_OPTIONS: { value: Severity; label: string; color: string }[] = [
  { value: 'error', label: 'Error', color: '#f87171' },
  { value: 'warning', label: 'Warning', color: '#facc15' },
  { value: 'info', label: 'Info', color: '#60a5fa' },
];

function SeverityPicker({
  value,
  onChange,
  disabled,
}: {
  value: Severity;
  onChange: (v: Severity) => void;
  disabled?: boolean;
}) {
  return (
    <div className={s.severityPicker}>
      {SEVERITY_OPTIONS.map((opt) => (
        <Button
          key={opt.value}
          type="button"
          variant={value === opt.value ? 'secondary' : 'ghost'}
          size="xs"
          className={s.severityOption}
          data-active={value === opt.value ? true : undefined}
          data-severity={opt.value}
          disabled={disabled}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  );
}

function PolicyReadinessPanel({
  items,
  counts,
}: {
  items: PolicyReadinessItem[];
  counts: PolicyStatusCounts;
}) {
  return (
    <Card
      as="section"
      variant="outlined"
      padding="none"
      className={s.policyReadiness}
      aria-label="Policy readiness"
    >
      <Stack gap="sm">
        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify="between"
          gap="md"
          className={s.policyReadinessHeader}
        >
          <Stack gap="xs">
            <Text
              as="h4"
              size="sm"
              weight="semibold"
              className={s.policyReadinessTitle}
            >
              Policy readiness
            </Text>
            <Text
              as="p"
              size="xs"
              color="secondary"
              className={s.policyReadinessDesc}
            >
              Rule labels are counted here; source cards explain the evidence
              behind each validator family.
            </Text>
          </Stack>
          <Stack
            direction="row"
            wrap
            gap="xs"
            className={s.policyReadinessCounts}
          >
            {(
              [
                'declared',
                'inferred',
                'generic_default',
                'missing',
                'not_required',
              ] as PolicySourceStatus[]
            ).map((status) => (
              <Text
                as="span"
                size="2xs"
                weight="semibold"
                key={status}
                className={s.policyReadinessCount}
                data-status={status}
              >
                {counts[status]} {POLICY_STATUS_LABELS[status]}
              </Text>
            ))}
          </Stack>
        </Stack>

        <Stack className={s.policyReadinessRows} gap="sm">
          {items.map((item) => (
            <Card
              key={item.id}
              as="article"
              variant="outlined"
              padding="none"
              className={s.policyReadinessRow}
            >
              <Stack className={s.policyReadinessRowMain} gap="xs">
                <Text
                  as="span"
                  size="xs"
                  weight="semibold"
                  className={s.policyReadinessRowLabel}
                >
                  {item.label}
                </Text>
                <Text
                  as="span"
                  size="xs"
                  color="secondary"
                  className={s.policyReadinessRowDetail}
                >
                  {item.detail}
                </Text>
              </Stack>
              <Text
                as="span"
                size="2xs"
                weight="semibold"
                className={s.policySourceBadge}
                data-status={item.status}
              >
                {POLICY_STATUS_LABELS[item.status]}
              </Text>
            </Card>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

const CODE_POLICY_GROUPS = groupCodePolicyByFamily(GOVERNANCE_CODE_CATALOG);

function CodePolicySection({
  group,
  policy,
  policyData,
  collapsed,
  disabled,
  onToggle,
  onChange,
}: {
  group: {
    family: string;
    label: string;
    entries: GovernanceCodeCatalogEntry[];
  };
  policy: PolicyState;
  policyData: GovernancePolicyQueryData;
  collapsed: boolean;
  disabled?: boolean;
  onToggle: () => void;
  onChange: (code: string, next: RuleState) => void;
}) {
  const enabledCount = group.entries.filter(
    (entry) => policy[entry.code]?.enabled,
  ).length;

  return (
    <Card
      as="section"
      variant="outlined"
      padding="none"
      className={s.codeCatalogSection}
    >
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={s.codePolicyHeaderButton}
        onClick={onToggle}
        aria-expanded={!collapsed}
      >
        <Text as="span" size="sm" weight="semibold">
          {collapsed ? <CaretRight size={13} /> : <CaretDown size={13} />}
          {group.label}
        </Text>
        <Text as="small" size="xs" color="tertiary">
          {enabledCount}/{group.entries.length} enabled
        </Text>
      </Button>
      {!collapsed && (
        <div className={s.codesTableShell}>
          <table className={s.codesTable}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Finding</th>
                <th>Rule</th>
                <th>Policy</th>
                <th>Severity</th>
                <th>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {group.entries.map((entry) => {
                const state =
                  policy[entry.code] ?? DEFAULT_CODE_STATES[entry.code];
                const status = getCodePolicyStatus(entry, policyData);
                return (
                  <tr key={entry.code}>
                    <td>
                      <Text
                        as="span"
                        size="2xs"
                        weight="semibold"
                        className={s.codePill}
                      >
                        {entry.code}
                      </Text>
                    </td>
                    <td>
                      <div className={s.codeTitleCell}>
                        <Text as="strong" size="xs" weight="semibold">
                          {entry.title}
                        </Text>
                        <Text as="span" size="2xs" color="tertiary">
                          {entry.familyLabel}
                        </Text>
                      </div>
                    </td>
                    <td>
                      <code className={s.ruleId}>{entry.ruleId}</code>
                    </td>
                    <td>
                      <Text
                        as="span"
                        size="2xs"
                        weight="semibold"
                        className={s.policySourceBadge}
                        data-status={status}
                      >
                        {POLICY_STATUS_LABELS[status]}
                      </Text>
                    </td>
                    <td>
                      <SeverityPicker
                        value={state.severity}
                        onChange={(severity) =>
                          onChange(entry.code, { ...state, severity })
                        }
                        disabled={disabled || !state.enabled}
                      />
                    </td>
                    <td>
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        className={s.ruleCardToggle}
                        onClick={() =>
                          onChange(entry.code, {
                            ...state,
                            enabled: !state.enabled,
                          })
                        }
                        disabled={disabled}
                        aria-label={`${state.enabled ? 'Disable' : 'Enable'} ${entry.code}`}
                      >
                        <span
                          className={s.ruleToggleTrack}
                          data-on={state.enabled ? true : undefined}
                        >
                          <span className={s.ruleToggleThumb} />
                        </span>
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function groupCodePolicyByFamily(
  codes: readonly GovernanceCodeCatalogEntry[],
): Array<{
  family: string;
  label: string;
  entries: GovernanceCodeCatalogEntry[];
}> {
  const map = new Map<string, GovernanceCodeCatalogEntry[]>();
  for (const entry of codes) {
    const group = map.get(entry.family) ?? [];
    group.push(entry);
    map.set(entry.family, group);
  }
  return [...map.entries()].map(([family, entries]) => ({
    family,
    label: entries[0]?.familyLabel ?? family,
    entries,
  }));
}

// ── Main export ─────────────────────────────────────────────────

export function GovernanceRules({ projectId }: { projectId: Id<'projects'> }) {
  const { activeRole, org } = useOrgContext();
  const { user } = useClerkUser();
  const isAdmin = activeRole === 'owner' || activeRole === 'admin';
  const plan = (org?.plan ?? 'free') as PlanId;
  const canManageCloudPolicy = hasCloudPolicyManagement(plan);

  const policyData = useQuery(api.projects.getGovernancePolicy, {
    projectId,
    clerkUserId: user?.id,
  });
  const updatePolicy = useMutation(api.projects.updateGovernancePolicy);

  const [localPolicy, setLocalPolicy] = React.useState<PolicyState | null>(
    null,
  );
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [collapsedFamilies, setCollapsedFamilies] = React.useState<Set<string>>(
    () => new Set(),
  );

  const configDetected = policyData?.configDetected ?? false;

  React.useEffect(() => {
    if (policyData && !localPolicy) {
      setLocalPolicy(
        policyFromConvex(
          policyData.policy?.rules ?? null,
          policyData.policy?.codes ?? null,
        ),
      );
    }
  }, [policyData, localPolicy]);

  const policy = localPolicy ?? getDefaultPolicy();

  function handleCodeChange(code: string, next: RuleState) {
    const updated = { ...policy, [code]: next };
    setLocalPolicy(updated);
    setDirty(true);
    if (saveError) setSaveError(null);
  }

  function toggleFamily(family: string) {
    setCollapsedFamilies((current) => {
      const next = new Set(current);
      if (next.has(family)) next.delete(family);
      else next.add(family);
      return next;
    });
  }

  async function handleSave() {
    if (!isAdmin || !localPolicy || !user?.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (shouldForcePolicySaveFailure()) {
        throw new Error('Forced policy save failure');
      }
      await updatePolicy({
        projectId,
        clerkUserId: user.id,
        codes: policyToConvexCodes(localPolicy),
      });
      setDirty(false);
    } catch (err) {
      // Surface the error so the user knows the save didn't land. We
      // keep localPolicy + dirty intact so edits aren't lost; the user
      // can retry or abandon.
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (!policyData) {
    return (
      <Text as="div" size="sm" color="tertiary" className={s.rulesLoading}>
        <CircleNotch size={14} className={s.spinIcon} />
        Loading rules&hellip;
      </Text>
    );
  }

  const readOnly = !isAdmin || configDetected || !canManageCloudPolicy;
  const policyQueryData = policyData as GovernancePolicyQueryData;
  const readinessItems = buildPolicyReadinessItems(policyQueryData);
  const readinessCounts = buildCodePolicyStatusCounts(policyQueryData);

  return (
    <Card
      as="section"
      variant="outlined"
      padding="none"
      className={s.rulesSection}
    >
      <Stack gap="md">
        <Stack
          direction={{ base: 'column', md: 'row' }}
          justify="between"
          gap="md"
          className={s.rulesSectionHeader}
        >
          <Stack gap="xs">
            <Text
              as="h3"
              size="sm"
              weight="semibold"
              className={s.rulesSectionTitle}
            >
              <ShieldCheck size={16} weight="duotone" />
              Rules
            </Text>
            <Text
              as="p"
              size="xs"
              color="secondary"
              className={s.rulesSectionDesc}
            >
              Configure which FUI codes run on your codebase, grouped by
              validator family.
            </Text>
          </Stack>
          {dirty && isAdmin && !configDetected && canManageCloudPolicy && (
            <Button
              type="button"
              variant="primary"
              size="xs"
              className={s.rulesSaveBtn}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <CircleNotch size={11} className={s.spinIcon} />
                  Saving&hellip;
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          )}
        </Stack>

        {configDetected && (
          <Text as="div" size="xs" className={s.configOverrideBanner}>
            <Info size={14} weight="fill" />
            <div>
              <Text as="strong" size="xs" weight="semibold">
                Config file detected.
              </Text>{' '}
              Rules are managed by <code>fragments.config.ts</code> in your
              repository. Changes here are overridden by the config file at CI
              time.
            </div>
          </Text>
        )}

        {saveError && (
          <Text
            as="div"
            size="xs"
            className={s.configOverrideBanner}
            role="alert"
            data-severity="error"
          >
            <Warning size={14} weight="fill" />
            <div>
              <Text as="strong" size="xs" weight="semibold">
                Couldn&apos;t save changes.
              </Text>{' '}
              {saveError}
            </div>
          </Text>
        )}

        {!configDetected && !isAdmin && (
          <Text as="div" size="xs" className={s.configOverrideBanner}>
            <Info size={14} weight="fill" />
            <Text as="span" size="xs">
              Only admins and owners can edit governance rules.
            </Text>
          </Text>
        )}

        {!configDetected && isAdmin && !canManageCloudPolicy && (
          <Text as="div" size="xs" className={s.configOverrideBanner}>
            <Info size={14} weight="fill" />
            <Text as="span" size="xs">
              Cloud policy editing requires{' '}
              {PLANS[cheapestPlanWithCloudPolicyManagement() ?? 'pro'].label}.
              Your {PLANS[plan].label} workspace can still use local{' '}
              <code>fragments.config.ts</code> rules.
            </Text>
          </Text>
        )}

        <PolicyReadinessPanel items={readinessItems} counts={readinessCounts} />

        <div className={s.codeCatalogSections}>
          {CODE_POLICY_GROUPS.map((group) => (
            <CodePolicySection
              key={group.family}
              group={group}
              policy={policy}
              policyData={policyQueryData}
              collapsed={collapsedFamilies.has(group.family)}
              onToggle={() => toggleFamily(group.family)}
              onChange={handleCodeChange}
              disabled={readOnly}
            />
          ))}
        </div>

        <Text as="div" size="xs" color="tertiary" className={s.rulesPrecedence}>
          <Warning size={12} />
          <Text as="span" size="xs" color="tertiary">
            {configDetected
              ? "Your repository's fragments.config.ts takes precedence over these settings."
              : 'These settings apply when no fragments.config.ts exists in the repository. Add a config file to override.'}
          </Text>
        </Text>
      </Stack>
    </Card>
  );
}
